/**
 * Title Animation - Original Implementation
 * Direct port from the original script.js title_animation function
 * Handles GSAP SplitText animations for section titles
 */

// Declare global types
declare global {
    interface Window {
        gsap: any;
        SplitText: any;
        ScrollTrigger: any;
        Back: any;
        jQuery: any;
        titleAnimationTimeout?: number;
    }
}

let isAnimating = false;
let animationTimeout: number | null = null;
const animatedElements = new Set<Element>(); // Track which elements have been animated

/**
 * Check if device is mobile
 */
function isMobileDevice(): boolean {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * Initialize title animations using GSAP and SplitText
 * This is the EXACT implementation from the original script.js with debouncing
 * Fixed for mobile to prevent stuttering/re-rendering
 */
export function initializeTitleAnimation(): void {
    // Debounce multiple rapid calls - longer delay on mobile
    if (animationTimeout) {
        clearTimeout(animationTimeout);
    }

    const delay = isMobileDevice() ? 300 : 100;
    animationTimeout = window.setTimeout(() => {
        _runTitleAnimation();
    }, delay) as unknown as number;
}

/**
 * Update title text content without re-animating
 * This is used when language changes to update text while preserving animation state
 */
export function updateTitleTextOnly(): void {
    // Wait for GSAP and SplitText to be available
    if (typeof window.gsap === 'undefined' || typeof window.SplitText === 'undefined') {
        return;
    }

    const quotes = document.querySelectorAll(".sec-title-animation .title-animation");
    
    if (quotes.length === 0) {
        return;
    }

    quotes.forEach((quote: Element) => {
        const quoteElement = quote as any;
        
        // If there's an existing split, revert it completely
        if (quoteElement.split) {
            // Kill the animation to prevent conflicts
            if (quoteElement.animation) {
                quoteElement.animation.kill();
                quoteElement.animation = null;
            }
            
            // Revert the split to restore original DOM structure
            quoteElement.split.revert();
            quoteElement.split = null;
        }
        
        // Reset any inline styles that might have been applied
        quoteElement.style.opacity = '1';
        quoteElement.style.transform = '';
        quoteElement.style.perspective = '';
        
        // Force the element to show with full opacity immediately
        // This ensures the new text (updated by React) is visible
        setTimeout(() => {
            if (quoteElement.style) {
                quoteElement.style.opacity = '1';
            }
        }, 0);
    });
}

function _runTitleAnimation(): void {
    // Prevent overlapping animations
    if (isAnimating) {
        return;
    }

    // Wait for GSAP and SplitText to be available
    if (typeof window.gsap === 'undefined' || typeof window.SplitText === 'undefined') {
        setTimeout(() => _runTitleAnimation(), 100);
        return;
    }

    const { gsap, SplitText, Back } = window;
    const isMobile = isMobileDevice();

    // Check if jQuery is available for the check
    if (typeof (window as any).jQuery !== 'undefined') {
        const tg_var = (window as any).jQuery('.sec-title-animation');
        if (!tg_var.length) {
            return;
        }
    } else {
        // Fallback to native JS if jQuery is not available
        if (document.querySelectorAll('.sec-title-animation').length === 0) {
            return;
    }
    }

    const quotes = document.querySelectorAll(".sec-title-animation .title-animation");

    if (quotes.length === 0) {
        return;
    }

    isAnimating = true;

    quotes.forEach((quote: Element) => {
        const quoteElement = quote as any;
        
        // Skip if already animated (prevents re-animation on mobile)
        if (animatedElements.has(quoteElement)) {
            return;
        }
        
        // Reset if needed (original implementation)
            if (quoteElement.animation) {
            quoteElement.animation.progress(1).kill();
            if (quoteElement.split) {
                quoteElement.split.revert();
            }
        }

        const getclass = quoteElement.closest('.sec-title-animation')?.className || '';
        const animation = getclass.split('animation-');
        if (animation[1] == "style4") return;

        quoteElement.split = new SplitText(quoteElement, {
            type: "lines,words,chars",
            linesClass: "split-line"
        });

        gsap.set(quoteElement, {
            perspective: 400
        });

        if (animation[1] == "style1") {
            gsap.set(quoteElement.split.chars, {
                opacity: 0,
                y: "90%",
                rotateX: "-40deg"
            });
        }
        if (animation[1] == "style2") {
            gsap.set(quoteElement.split.chars, {
                opacity: 0,
                x: "50"
            });
        }
        if (animation[1] == "style3") {
            gsap.set(quoteElement.split.chars, {
                opacity: 0,
            });
        }

        // On mobile, use IntersectionObserver instead of ScrollTrigger to prevent stuttering
        if (isMobile) {
            // Use IntersectionObserver on mobile to trigger when element comes into view
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && !animatedElements.has(quoteElement)) {
                            // Element is in view, trigger animation
                            quoteElement.animation = gsap.to(quoteElement.split.chars, {
                                x: "0",
                                y: "0",
                                rotateX: "0",
                                opacity: 1,
                                duration: 1,
                                ease: Back.easeOut,
                                stagger: .02,
                                onComplete: () => {
                                    // Mark as animated
                                    animatedElements.add(quoteElement);
                                    observer.unobserve(quoteElement);
                                }
                            });
                        }
                    });
                },
                {
                    threshold: 0.1, // Trigger when 10% of element is visible
                    rootMargin: '50px' // Start a bit earlier
                }
            );
            
            // Check if already visible, if so animate immediately
            const rect = quoteElement.getBoundingClientRect();
            const isVisible = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
            
            if (isVisible) {
                // Already visible, animate immediately
                quoteElement.animation = gsap.to(quoteElement.split.chars, {
                    x: "0",
                    y: "0",
                    rotateX: "0",
                    opacity: 1,
                    duration: 1,
                    ease: Back.easeOut,
                    stagger: .02,
                    onComplete: () => {
                        animatedElements.add(quoteElement);
                    }
                });
            } else {
                // Not visible yet, observe for when it comes into view
                observer.observe(quoteElement);
            }
        } else {
            // Desktop: use ScrollTrigger
        quoteElement.animation = gsap.to(quoteElement.split.chars, {
            scrollTrigger: {
                trigger: quoteElement,
                start: "top 90%",
                    once: true, // Only trigger once
            },
            x: "0",
            y: "0",
            rotateX: "0",
            opacity: 1,
            duration: 1,
            ease: Back.easeOut,
                stagger: .02,
                onComplete: () => {
                    // Mark as animated
                    animatedElements.add(quoteElement);
                }
        });
        }
    });

    // Release the lock after animations are set up
    setTimeout(() => {
        isAnimating = false;
    }, 200);
}

// Expose function globally and add ScrollTrigger listener (original implementation)
if (typeof window !== 'undefined') {
    (window as any).title_animation = initializeTitleAnimation;
    
    // Add the ScrollTrigger listener (matches original) - only once
    // On mobile, don't listen to ScrollTrigger refresh to prevent stuttering
    if (typeof window.ScrollTrigger !== 'undefined' && !(window as any).titleAnimationListenerAdded) {
        const isMobile = isMobileDevice();
        if (!isMobile) {
            // Only add ScrollTrigger refresh listener on desktop
        window.ScrollTrigger.addEventListener("refresh", initializeTitleAnimation);
        }
        (window as any).titleAnimationListenerAdded = true;
    }
}
